import { HardDriveUpload, CloudDownload, Copy, Check } from "lucide-react";
import { AppColorVariant } from "../theme/AppColorVariant";
import { ConflictReason } from "./ConflictReason";

export enum ConflictResolutionAction {
  ReplaceFromDisk = "replaceFromDisk",
  AcceptSharePoint = "acceptSharePoint",
  RenameKeepBoth = "renameKeepBoth",
  SkipAcknowledge = "skipAcknowledge",
}

export const ConflictResolutionActionLabels: Record<ConflictResolutionAction, string> = {
  [ConflictResolutionAction.ReplaceFromDisk]: "Replace from disk",
  [ConflictResolutionAction.AcceptSharePoint]: "Accept SharePoint copy",
  [ConflictResolutionAction.RenameKeepBoth]: "Rename & keep both",
  [ConflictResolutionAction.SkipAcknowledge]: "Skip & acknowledge",
};

export const ConflictResolutionActionColors: Record<ConflictResolutionAction, AppColorVariant> = {
  [ConflictResolutionAction.ReplaceFromDisk]: "danger",
  [ConflictResolutionAction.AcceptSharePoint]: "primary",
  [ConflictResolutionAction.RenameKeepBoth]: "success",
  [ConflictResolutionAction.SkipAcknowledge]: "neutral",
};

export const ConflictResolutionActionIcons: Record<ConflictResolutionAction, React.ElementType> = {
  [ConflictResolutionAction.ReplaceFromDisk]: HardDriveUpload,
  [ConflictResolutionAction.AcceptSharePoint]: CloudDownload,
  [ConflictResolutionAction.RenameKeepBoth]: Copy,
  [ConflictResolutionAction.SkipAcknowledge]: Check,
};

/** Short helper text shown under each action in the resolve menu. */
export const ConflictResolutionActionDescriptions: Record<ConflictResolutionAction, string> = {
  [ConflictResolutionAction.ReplaceFromDisk]:
    "Overwrite the SharePoint copy with the local file.",
  [ConflictResolutionAction.AcceptSharePoint]:
    "Keep the SharePoint copy and stop migrating the local file.",
  [ConflictResolutionAction.RenameKeepBoth]:
    "Upload the local file under a new name, keeping both copies.",
  [ConflictResolutionAction.SkipAcknowledge]:
    "Leave both copies as-is and mark the conflict acknowledged.",
};

/** ReplaceFromDisk is destructive (it overwrites the SharePoint copy / occupying item), so it warrants a confirmation step. */
export const isDestructiveResolution = (
  action: ConflictResolutionAction
): boolean => action === ConflictResolutionAction.ReplaceFromDisk;

/**
 * The actions offered for a file in the resolve drawer: any known conflict reason offers all four (a loser
 * can adopt an occupant or the winner; whether a winner is required is checked at submit time), while an
 * unknown reason offers only the non-destructive actions.
 */
export const getAllowedResolutionActions = (
  reason: ConflictReason | null
): ConflictResolutionAction[] =>
  reason == null
    ? [
        ConflictResolutionAction.AcceptSharePoint,
        ConflictResolutionAction.RenameKeepBoth,
        ConflictResolutionAction.SkipAcknowledge,
      ]
    : [
        ConflictResolutionAction.ReplaceFromDisk,
        ConflictResolutionAction.AcceptSharePoint,
        ConflictResolutionAction.RenameKeepBoth,
        ConflictResolutionAction.SkipAcknowledge,
      ];

/**
 * The action a file starts on before the operator chooses — always the non-destructive "Accept SharePoint copy".
 * A content mismatch adopts SharePoint's copy; a path collision adopts an existing occupant when there is one,
 * and otherwise stays invalid until a winner is nominated — so a genuine collision is never silently renamed.
 * Rename & keep both remains available, it is just never the default.
 */
export const defaultResolutionAction = (
  _reason: ConflictReason | null
): ConflictResolutionAction => ConflictResolutionAction.AcceptSharePoint;

/**
 * Whether resolving with this action actually transfers the local file's content to SharePoint. Mirrors the
 * server's ConflictResolutionPolicy.TransfersFileContent — only a replace or a rename move bytes.
 */
export const transfersFileContent = (action: ConflictResolutionAction): boolean =>
  action === ConflictResolutionAction.ReplaceFromDisk ||
  action === ConflictResolutionAction.RenameKeepBoth;

/**
 * How an outcome should be described once applied. These are genuinely different results and must not be
 * flattened into "queued for re-upload": only the first two queue anything, and SkipAcknowledge leaves the file
 * excluded from upload altogether because it lands on Acknowledged rather than Resolved.
 */
export const ConflictResolutionOutcomeLabels: Record<ConflictResolutionAction, string> = {
  [ConflictResolutionAction.ReplaceFromDisk]: "queued to overwrite the SharePoint copy",
  [ConflictResolutionAction.RenameKeepBoth]: "queued to upload under a new name",
  [ConflictResolutionAction.AcceptSharePoint]: "kept the SharePoint copy \u2014 nothing will upload",
  [ConflictResolutionAction.SkipAcknowledge]: "acknowledged and left as-is \u2014 still excluded from upload",
};

/** Short form for the pre-submit summary, where the reason label already gives the context. */
export const ConflictResolutionOutcomeShortLabels: Record<ConflictResolutionAction, string> = {
  [ConflictResolutionAction.ReplaceFromDisk]: "will be re-uploaded, overwriting SharePoint",
  [ConflictResolutionAction.RenameKeepBoth]: "will be uploaded under a new name",
  [ConflictResolutionAction.AcceptSharePoint]: "will adopt the SharePoint copy, uploading nothing",
  [ConflictResolutionAction.SkipAcknowledge]: "will be acknowledged and stay excluded from upload",
};
